/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-extraneous-dependencies */
import 'maplibre-gl/dist/maplibre-gl.css'
import '@maplibre/maplibre-gl-leaflet'
import {
  type LayerProps,
  createElementObject,
  createTileLayerComponent,
  withPane,
} from '@react-leaflet/core'
import L from 'leaflet'
import { useEffect, useState } from 'react'
import DebouncedRefreshOnResize from 'components/DebouncedRefreshOnResize'
import { useMap } from 'react-leaflet'
import 'leaflet.gridlayer.googlemutant/dist/Leaflet.GoogleMutant'
import { StyleSpecification } from 'maplibre-gl'
import { getProtomapStyle } from 'assets/js/protomap-styles'
import { Box, Center, Spinner } from '@chakra-ui/react'

export interface MapLibreTileLayerProps
  extends L.LeafletMaplibreGLOptions,
    LayerProps {
  url?: string
  style?: StyleSpecification
  attribution: string
  onRateLimitError?: (errorMessage: string) => void
}

const MapLibreTileLayer = createTileLayerComponent<
  L.MaplibreGL,
  MapLibreTileLayerProps & {
    onError?: (e: ErrorEvent) => void
  }
>(
  ({ url, style, onError, ...options }, context) => {
    const layer = L.maplibreGL({ style: style || url })
    Object.assign(layer.options, withPane(options, context))

    layer.on('add', () => {
      const mapLibre = layer.getMaplibreMap()
      mapLibre.on('error', (e) => {
        if (onError) {
          onError(e)
        }
      })
    })

    return createElementObject(layer, context)
  },
  (layer, props, prevProps) => {
    const { url, style, attribution } = props
    if (url != null && url !== prevProps.url) {
      layer.getMaplibreMap().setStyle(style || url)
    }

    if (attribution != null && attribution !== prevProps.attribution) {
      layer.options.attribution = attribution
    }
  }
)

const withValidatePmtiles = (WrappedComponent: any) => {
  const ValidatedMapLibreTileLayer = (props: MapLibreTileLayerProps) => {
    const { onRateLimitError, style, ...rest } = props
    const [validatedTiles, setValidatedTiles] = useState<
      'protomaps' | 'openfreemap' | null
    >(null)

    const handleError = (event: ErrorEvent) => {
      const msg =
        event?.error?.message?.toLowerCase() ??
        event?.error?.toString().toLowerCase()

      if (
        event?.error ||
        event?.error?.status === 404 ||
        event?.error?.status === 429 ||
        event?.error?.status === 0 ||
        msg.includes('404') ||
        msg.includes('failed to fetch') ||
        msg.includes('limit') ||
        msg.includes('too many')
      ) {
        if (onRateLimitError && validatedTiles === 'openfreemap') {
          const message = `Rate limit reached, switching to dynamic JS API - ${msg}`
          onRateLimitError(message)
        } else {
          setValidatedTiles('openfreemap')
        }
      }
    }

    useEffect(() => {
      let isMounted = true

      const validate = async () => {
        const pmtilesUrl = `${process.env.REACT_APP_MAP_URL}/jawa.pmtiles`

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 3000)

        try {
          const res = await fetch(pmtilesUrl, {
            method: 'HEAD',
            signal: controller.signal,
          })
          clearTimeout(timeoutId)
          if (res.ok) {
            if (isMounted) setValidatedTiles('protomaps')
          } else {
            if (isMounted) setValidatedTiles('openfreemap')
          }
        } catch (err) {
          clearTimeout(timeoutId)
          if (isMounted) setValidatedTiles('openfreemap')
        }
      }

      if (style) {
        validate()
      } else {
        setValidatedTiles('openfreemap')
      }

      return () => {
        isMounted = false
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (!validatedTiles)
      return (
        <Box inset={0} zIndex={9999} position="absolute">
          <Center h="full" bg="white">
            <Spinner size="xl" color="royalblue" />
          </Center>
        </Box>
      )

    return (
      <>
        <DebouncedRefreshOnResize />
        {validatedTiles === 'protomaps' ? (
          <WrappedComponent
            {...rest}
            style={props?.style || getProtomapStyle()}
            url={props?.url}
            onError={handleError}
          />
        ) : (
          <WrappedComponent
            {...rest}
            url="/map-styles.json"
            onError={handleError}
          />
        )}
      </>
    )
  }

  return ValidatedMapLibreTileLayer
}

export const GoogleMapTileLayer = () => {
  const map = useMap()

  useEffect(() => {
    const layer = L.gridLayer.googleMutant({
      type: 'roadmap',
      styles: [
        {
          featureType: 'administrative',
          elementType: 'all',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'administrative',
          elementType: 'geometry.fill',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'administrative',
          elementType: 'geometry.stroke',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'administrative',
          elementType: 'labels.text',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'landscape',
          elementType: 'all',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'poi',
          elementType: 'all',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'poi.business',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'poi.place_of_worship',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'poi.school',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'road',
          elementType: 'all',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'transit',
          elementType: 'all',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'water',
          elementType: 'all',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
      ],
    })

    layer.addTo(map)

    return () => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer)
      }
    }
  }, [map])

  return null
}

export const ProtomapsTileLayer = withValidatePmtiles(MapLibreTileLayer)

export default MapLibreTileLayer
